// ============================================
// Document Operations Firestore Service
// CRUD for brands, documents (subcollection),
// templates, and categories.
// ============================================

import {
  collection, doc, getDocs, getDoc, setDoc, deleteDoc,
  writeBatch, orderBy, query,
} from 'firebase/firestore';
import { db } from '../firebase';
import { logAudit } from '../../utils/auditLog';
import {
  C_DOC_BRANDS,
  C_DOC_CATEGORIES,
  C_DOC_OWNERS,
  C_DOC_TEMPLATES,
  SC_DOCUMENTS,
} from '../../constants/collections';
import type {
  DocBrand, DocDocument, DocTemplate, DocCategory, OwnerProfile,
} from '../../types/docops';

// ── Collection helpers ──

const ownersCol = () => collection(db, C_DOC_OWNERS);
const brandsCol = () => collection(db, C_DOC_BRANDS);
const templatesCol = () => collection(db, C_DOC_TEMPLATES);
const categoriesCol = () => collection(db, C_DOC_CATEGORIES);
const brandDocsCol = (brandId: string) =>
  collection(db, C_DOC_BRANDS, brandId, SC_DOCUMENTS);

// ── Owner Profiles ──

export async function getOwnerProfile(ownerId: string): Promise<OwnerProfile | null> {
  const snap = await getDoc(doc(ownersCol(), ownerId));
  return snap.exists() ? (snap.data() as OwnerProfile) : null;
}

export async function saveOwnerProfile(profile: OwnerProfile): Promise<void> {
  await setDoc(doc(ownersCol(), profile.ownerId), stripUndefined(profile));
  logAudit('data.modify', 'doc_ops', {
    action: 'owner_profile_saved',
    ownerId: profile.ownerId,
  });
}

export async function seedOwnerProfile(
  profile: OwnerProfile,
  onProgress?: (msg: string) => void,
): Promise<void> {
  await setDoc(doc(ownersCol(), profile.ownerId), stripUndefined(profile));
  onProgress?.(`Owner profile seeded: ${profile.ownerId}`);
  logAudit('data.modify', 'doc_ops', {
    action: 'owner_profile_seeded',
    ownerId: profile.ownerId,
  });
}

// ── Brands ──

export async function getAllBrands(): Promise<DocBrand[]> {
  const snap = await getDocs(query(brandsCol(), orderBy('name')));
  return snap.docs.map((d) => d.data() as DocBrand);
}

export async function getBrand(id: string): Promise<DocBrand | null> {
  const snap = await getDoc(doc(brandsCol(), id));
  return snap.exists() ? (snap.data() as DocBrand) : null;
}

export async function saveBrand(brand: DocBrand): Promise<void> {
  await setDoc(doc(brandsCol(), brand.id), stripUndefined(brand));
  logAudit('data.modify', 'doc_ops', {
    action: 'brand_saved',
    brandId: brand.id,
    brandName: brand.name,
  });
}

export async function deleteBrand(id: string): Promise<void> {
  await deleteDoc(doc(brandsCol(), id));
  logAudit('data.delete', 'doc_ops', { action: 'brand_deleted', brandId: id });
}

// ── Documents (subcollection per brand) ──

export async function getDocumentsByBrand(brandId: string): Promise<DocDocument[]> {
  const snap = await getDocs(query(brandDocsCol(brandId), orderBy('categoryId')));
  return snap.docs.map((d) => d.data() as DocDocument);
}

export async function getDocument(
  brandId: string,
  docId: string,
): Promise<DocDocument | null> {
  const snap = await getDoc(doc(brandDocsCol(brandId), docId));
  return snap.exists() ? (snap.data() as DocDocument) : null;
}

export async function saveDocument(
  brandId: string,
  document: DocDocument,
): Promise<void> {
  await setDoc(
    doc(brandDocsCol(brandId), document.id),
    stripUndefined(document),
  );
  logAudit('data.modify', 'doc_ops', {
    action: 'document_saved',
    brandId,
    documentId: document.id,
    status: document.status,
  });
}

export async function deleteDocument(
  brandId: string,
  docId: string,
): Promise<void> {
  await deleteDoc(doc(brandDocsCol(brandId), docId));
  logAudit('data.delete', 'doc_ops', {
    action: 'document_deleted',
    brandId,
    documentId: docId,
  });
}

// ── Templates ──

export async function getAllTemplates(): Promise<DocTemplate[]> {
  const snap = await getDocs(query(templatesCol(), orderBy('sortOrder')));
  return snap.docs.map((d) => d.data() as DocTemplate);
}

export async function getTemplatesByCategory(
  categoryId: string,
): Promise<DocTemplate[]> {
  const all = await getAllTemplates();
  return all.filter((t) => t.categoryId === categoryId);
}

// ── Categories ──

export async function getAllCategories(): Promise<DocCategory[]> {
  const snap = await getDocs(query(categoriesCol(), orderBy('sortOrder')));
  return snap.docs.map((d) => d.data() as DocCategory);
}

// ── Seed functions ──

export async function seedTemplates(
  templates: DocTemplate[],
  onProgress?: (msg: string) => void,
): Promise<number> {
  const batchSize = 450;
  for (let i = 0; i < templates.length; i += batchSize) {
    const batch = writeBatch(db);
    const chunk = templates.slice(i, i + batchSize);
    for (const t of chunk) {
      batch.set(doc(templatesCol(), t.id), stripUndefined(t));
    }
    await batch.commit();
    onProgress?.(`Templates batch ${Math.floor(i / batchSize) + 1}: ${chunk.length} written`);
  }
  onProgress?.(`Seeded ${templates.length} templates`);
  return templates.length;
}

export async function seedCategories(
  categories: DocCategory[],
  onProgress?: (msg: string) => void,
): Promise<number> {
  const batch = writeBatch(db);
  for (const cat of categories) {
    batch.set(doc(categoriesCol(), cat.id), stripUndefined(cat));
  }
  await batch.commit();
  onProgress?.(`Seeded ${categories.length} categories`);
  return categories.length;
}

export async function seedBrand(
  brand: DocBrand,
  templates: DocTemplate[],
  onProgress?: (msg: string) => void,
): Promise<number> {
  // Save brand document
  await setDoc(doc(brandsCol(), brand.id), stripUndefined(brand));
  onProgress?.(`Created brand: ${brand.name}`);

  // Initialize document stubs for every template
  const batchSize = 450;
  const now = new Date().toISOString();

  for (let i = 0; i < templates.length; i += batchSize) {
    const batch = writeBatch(db);
    const chunk = templates.slice(i, i + batchSize);
    for (const t of chunk) {
      const docStub: DocDocument = {
        id: `${brand.id}-${t.id}`,
        brandId: brand.id,
        templateId: t.id,
        categoryId: t.categoryId,
        title: t.title,
        status: 'not_started',
        version: 0,
        content: '',
        generatedAt: null,
        lastEditedAt: now,
        lastEditedBy: 'system',
        signedAt: null,
        signedBy: null,
        expiresAt: null,
        renewalReminderDays: null,
        attorneyReviewRequired: t.attorneyReviewRequired,
        attorneyReviewedAt: null,
        attorneyNotes: null,
        launchBlocker: t.launchBlocker,
        tags: [],
      };
      batch.set(
        doc(brandDocsCol(brand.id), docStub.id),
        stripUndefined(docStub),
      );
    }
    await batch.commit();
    onProgress?.(`Documents batch ${Math.floor(i / batchSize) + 1}: ${chunk.length} stubs created`);
  }

  onProgress?.(`Initialized ${templates.length} document stubs for ${brand.name}`);
  logAudit('data.modify', 'doc_ops', {
    action: 'brand_seeded',
    brandId: brand.id,
    templateCount: templates.length.toString(),
  });

  return templates.length;
}

// ── Utility ──

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object')
        ? stripUndefined(value)
        : value;
    }
  }
  return cleaned;
}
